import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from '../../../components/QRCode';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [showQR, setShowQR] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle newsletter subscription
    console.log('Newsletter subscription:', email);
    setEmail('');
  };

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Our Team', path: '/about#team' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
    { name: 'Careers', path: '/careers' }
  ];

  const handleQuickLinkClick = (path: string) => {
    if (path.includes('#')) {
      const [route, hash] = path.split('#');
      navigate(route);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      navigate(path);
    }
  };

  return (
    <footer id="contact" className="bg-gradient-to-br from-[#9B4C9A] to-[#FF9BC8] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Logo and Description */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://static.readdy.ai/image/8ba2fb49baf129fe47398873ffc1b622/ae217e5ef1f4a035b7b045b6b3280b3a.jpeg" 
                alt="Fluffy Pets Logo" 
                className="w-48 h-32 object-contain"
              />
            </div>
            <p className="text-white/90 leading-relaxed mb-6">
              Providing loving care for your furry family members with professional grooming, daycare, and boarding services.
            </p>
            <div className="flex gap-4 mb-6">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
              >
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
              >
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
              >
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
            </div>
            
            {/* QR Code Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => setShowQR(!showQR)}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-full text-sm font-medium transition-all hover:scale-105 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-qr-code-line text-lg"></i>
                {showQR ? 'Hide QR Code' : 'Show QR Code'}
              </button>
              
              <button
                onClick={() => navigate('/qr-print')}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-full text-sm font-medium transition-all hover:scale-105 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-printer-line text-lg"></i>
                Print QR Code
              </button>
            </div>
            
            {/* QR Code Display */}
            {showQR && (
              <div className="mt-6 bg-white p-4 rounded-2xl inline-block animate-fade-in">
                <QRCode size={180} />
                <p className="text-[#9B4C9A] text-xs text-center mt-3 font-medium">
                  Scan to visit our website
                </p>
              </div>
            )}
          </div>

          {/* Services Column */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold text-black font-heading">
              Our Services
            </h4>
            <ul className="space-y-3">
              {['Pet Grooming', 'Pet Boarding', 'Pet Supplies', 'Pet Food', 'Pet Accessories'].map((service) => (
                <li key={service}>
                  <button 
                    onClick={() => navigate('/services')}
                    className="text-white hover:text-[#FF9B9B] transition-colors flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold text-[#4A3B5C] font-heading">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => handleQuickLinkClick(link.path)}
                    className="text-white hover:text-white/80 transition-colors flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Column */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold text-[#4A3B5C] font-heading">
              Stay Connected
            </h4>
            <p className="text-white text-sm leading-relaxed">
              Subscribe to get pet care tips, special offers, and updates!
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full px-4 py-3 rounded-2xl bg-white text-[#4A3B5C] placeholder-[#8B7BA8]/50 focus:outline-none focus:ring-2 focus:ring-[#FF9B9B] text-sm shadow-sm"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#FF9B9B] text-white px-6 py-3 rounded-2xl hover:bg-[#FF8585] transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer font-medium"
              >
                Subscribe Now
                <i className="ri-send-plane-fill"></i>
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-8 mt-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/80 text-sm">
              © 2024 Fluffy Pets. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
